import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';

import { ListItemGroup } from 'components/list-item-group';
import { ProductNoteText } from 'components/product-detail/styled';
import { useProductPropsSelections } from 'components/product-detail/use-product-props-selections';
import { ProductListItem } from 'components/product-list-item';
import { View } from 'components/view';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard';
import { useScrollContext } from 'state/scroll';

import { IProductHomeProps } from '../product-home';

import { OrderSummaryContainer } from './order-summary.styled';
import { transformUserSelectionsToSummaryItems } from './utils';

export const OrderSummary: FC<IProductHomeProps> = ({ homePositionRef }) => {
  const divElementRef = useRef<HTMLDivElement>(null);
  const { selectedProduct, userSelections, goToCustomizeProduct } = useProductWizardContext();
  const { priceForComboSlotSelection } = useMenuContext();
  const { scrollTo } = useScrollContext();

  const { buildProductPropsSelections } = useProductPropsSelections();
  const { formatMessage } = useIntl();
  const { formatCalories } = useMenuCalories();

  const mappedSummaryItems = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }
    return transformUserSelectionsToSummaryItems({
      buildProductPropsSelections,
      priceForComboSlotSelection,
      selectedProduct,
      userSelections,
    });
  }, [buildProductPropsSelections, priceForComboSlotSelection, selectedProduct, userSelections]);

  const showCustomizableTitle = useMemo(
    () => mappedSummaryItems.some(item => item.productListItemProps.isClickable),
    [mappedSummaryItems]
  );

  const handleSummaryLineClick = useCallback(
    (slotKey: string) => {
      goToCustomizeProduct(slotKey);
      scrollTo({ y: 0, animated: false });
    },
    [goToCustomizeProduct, scrollTo]
  );

  useEffect(() => {
    scrollTo({ y: homePositionRef?.current ?? 0, animated: false });
  }, [homePositionRef, scrollTo]);

  useLayoutEffect(() => {
    const element = divElementRef?.current;

    if (!element) {
      return;
    }

    if (homePositionRef) {
      homePositionRef.current = element.offsetTop;
    }
  }, [homePositionRef]);

  if (!selectedProduct) {
    return null;
  }

  return (
    <View ref={divElementRef}>
      <OrderSummaryContainer data-testid="order-summary">
        <ListItemGroup
          header={formatMessage({
            id: showCustomizableTitle
              ? 'productDetailSummaryCustomizableTitle'
              : 'productDetailSummaryTitle',
          })}
          items={mappedSummaryItems.map(summaryItem => (
            <ProductListItem
              tabIndex={0}
              key={summaryItem.productListItemProps.productProps.name}
              {...summaryItem.productListItemProps}
              productProps={{
                ...summaryItem.productListItemProps.productProps,
                calories: formatCalories(summaryItem.productListItemProps.productProps.calories),
                note: (
                  <ProductNoteText>
                    {summaryItem.productListItemProps.productProps.note}
                  </ProductNoteText>
                ),
              }}
              controls={
                summaryItem.productListItemProps.isClickable && (
                  <Icon data-testid="next-icon" icon="next" color="icon-default" width="12px" />
                )
              }
              onProductClick={() => handleSummaryLineClick(summaryItem.slotKey)}
            />
          ))}
        />
      </OrderSummaryContainer>
    </View>
  );
};
